/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
\* =========================================================================== */

import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import Cropper from 'react-easy-crop';
import Resizer from 'react-image-file-resizer';
import { useTranslation } from 'react-i18next';
import { getOrientation } from 'get-orientation/browser';

// @mui material components
import Card from '@mui/material/Card';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import Icon from '@mui/material/Icon';
import Grid from '@mui/material/Grid';
import Slider from '@mui/material/Slider';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

// Material Dashboard 2 PRO React components
import MDButton from 'components/MDButton';
import { getCroppedImg, getRotatedImage } from 'components/UploadImage/CanvasUtil';

const ORIENTATION_TO_ANGLE = {
  3: 180,
  6: 90,
  8: -90,
};

function CustomerUpload({ uploadData }) {
  const { t } = useTranslation();
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [rotation, setRotation] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [modal, setModal] = useState(true);
  const [confirm, setConfirm] = useState(false);

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const cancel = () => {
    setImageSrc(null);
  };

  const resizeFile = (file) =>
    new Promise((resolve) => {
      Resizer.imageFileResizer(
        file,
        1024,
        1024,
        'JPEG',
        100,
        0,
        (uri) => {
          resolve(uri);
        },
        'base64'
      );
    });

  const upload = useCallback(async () => {
    try {
      const croppedImage = await getCroppedImg(imageSrc, croppedAreaPixels, rotation);

      fetch(croppedImage)
        .then((res) => res.blob())
        .then(async (data) => {
          try {
            setModal(false);
            const image = await resizeFile(data);
            uploadData(image);
            cancel();
          } catch (err) {
            console.log(err);
          }
        });
    } catch (e) {
      console.error(e);
    }
  }, [imageSrc, croppedAreaPixels, rotation]);

  const readFile = (file) => {
    return new Promise((resolve) => {
      // console.log(file)
      const reader = new FileReader();
      reader.addEventListener('load', () => resolve(reader.result), false);
      reader.readAsDataURL(file);
    });
  };

  const onFileChange = async (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      let imageDataUrl = await readFile(file);
      //   console.log(imageDataUrl);

      // apply rotation if needed
      const orientation = await getOrientation(file);
      const rotation = ORIENTATION_TO_ANGLE[orientation];
      if (rotation) {
        imageDataUrl = await getRotatedImage(imageDataUrl, rotation);
      }

      setImageSrc(imageDataUrl);
      setConfirm(false);
    }
  };

  return (
    <div>
      {imageSrc ? (
        <Dialog
          open={modal}
          maxWidth='lg'
          keepMounted
          aria-describedby='alert-dialog-slide-description'
        >
          <DialogTitle>
            {t(`upload.upload`)}
            <IconButton
              aria-label='close'
              onClick={cancel}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <Icon>close</Icon>
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            <Card sx={{ height: 500, width: 800 }}>
              <Cropper
                image={imageSrc}
                crop={crop}
                rotation={rotation}
                zoom={zoom}
                aspect={1}
                onCropChange={setCrop}
                onRotationChange={setRotation}
                onCropComplete={onCropComplete}
                onZoomChange={setZoom}
              />
            </Card>
            <Box>
              <Grid container px={3} pt={3} spacing={4}>
                <Grid item xs={6}>
                  <Typography component='div'>{t('upload.zoom')}</Typography>
                  <Slider
                    value={zoom}
                    min={1}
                    max={3}
                    step={0.1}
                    scale={(x) => x ** 100}
                    onChange={(e, zoom) => setZoom(zoom)}
                    aria-labelledby='image-zoom'
                  />
                </Grid>
                <Grid item xs={6}>
                  <Typography component='div'>{t('upload.rotation')}</Typography>
                  <Slider
                    value={rotation}
                    min={0}
                    max={360}
                    step={1}
                    scale={(x) => x ** 100}
                    onChange={(e, rotation) => setRotation(rotation)}
                    aria-labelledby='image-rotation'
                  />
                </Grid>
              </Grid>
            </Box>
          </DialogContent>
          <DialogActions>
            {confirm ? (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                <MDButton variant='gradient' color='error' onClick={cancel}>
                  <Icon>cancel</Icon>&nbsp;{t('upload.cancel')}
                </MDButton>
                <MDButton variant='gradient' color='success' onClick={upload}>
                  <Icon>file_upload</Icon>&nbsp;{t('upload.uploadconfirm')}
                </MDButton>
              </Box>
            ) : (
              <MDButton
                variant='text'
                color='success'
                onClick={() => (imageSrc ? setConfirm(true) : setConfirm(false))}
              >
                <Icon>file_upload</Icon>&nbsp;{t('upload.upload')}
              </MDButton>
            )}
          </DialogActions>
        </Dialog>
      ) : (
        <Grid container>
          <Grid item xs={12}>
            <input
              accept='image/*'
              style={{ display: 'none' }}
              id='file-open'
              multiple
              type='file'
              onChange={onFileChange}
            />
            <label htmlFor='file-open'>
              <MDButton variant='gradient' color='info' size='small' component='span'>
                <Icon>image</Icon>&nbsp;{t('upload.browse')}
              </MDButton>
            </label>
          </Grid>
        </Grid>
      )}
    </div>
  );
}

CustomerUpload.propTypes = {
  uploadData: PropTypes.func.isRequired,
};

export default CustomerUpload;
